import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Badge,
  Button,
  Card,
  Code,
  Group,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  BrandingApiError,
  getBranding,
  listBrandingOrganizations,
  listBrandingVersions,
  putBranding,
  uploadBrandingImage,
  type BrandingFieldError,
  type BrandingOrganization,
  type BrandingRecord,
  type UploadedImage,
} from "@/api/branding";
import { useIAMAuth } from "@/auth/iam";
import { DELIVERY_IMAGE_BUDGET_BYTES, formatBytes } from "@/branding/images";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t, type MessageKey } from "@/i18n";
import { ColorFields } from "@/pages/branding/ColorFields";
import { DoctorSignaturesCard } from "@/pages/branding/DoctorSignaturesCard";
import { DomainsCard } from "@/pages/branding/DomainsCard";
import { ImageField } from "@/pages/branding/ImageField";
import { PreviewPanel } from "@/pages/branding/PreviewPanel";
import { WordingEditor } from "@/pages/branding/WordingEditor";
import {
  SWITCH_FIELDS,
  draftOf,
  effectiveImage,
  fieldsOf,
  imageInfoOf,
  imageTotal,
  inherited,
  type BrandingDraft,
  type ImageField as ImageFieldName,
  type SwitchField,
  type SwitchValue,
  type TextField,
} from "@/pages/branding/draft";

/**
 * Branding, in the client portal (`/portal/branding`) and the staff admin area
 * (`/admin/branding`): Gate B white-labelling, plan B1 and B3, contract 4.9.
 *
 * **Who may brand what is the server's decision, never this page's.** A client admin's own
 * organisation comes from their sign-in, and the orchestrator refuses any organisation
 * outside it and the ones under it (D10), whatever is sent. The picker here offers only
 * that subtree to a client admin so the page never invites a refusal, and a Medicus admin
 * can also open an organisation by its id.
 *
 * A save is live at once and audited (D12): it is the organisation's full own set, an
 * empty field is unset and the organisation takes that unit from its parent (D3, D19),
 * except the master signature and the two domains, which are never inherited.
 */

export type BrandingMode = "portal" | "admin";

/** The organisations a client admin may brand: their own and every one under it. */
export function withinReach(
  rows: BrandingOrganization[],
  own: string | null,
): BrandingOrganization[] {
  if (own === null) return [];
  const parentOf = new Map(rows.map((r) => [r.organization_id, r.parent_id]));
  return rows.filter((row) => {
    const seen = new Set<string>();
    let at: string | null | undefined = row.organization_id;
    while (at !== null && at !== undefined && !seen.has(at)) {
      if (at === own) return true;
      seen.add(at);
      at = parentOf.get(at);
    }
    return false;
  });
}

const ERROR_TEXT: Record<string, MessageKey> = {
  too_long: "branding.error.too_long",
  empty: "branding.error.empty",
  control_character: "branding.error.control_character",
  not_https: "branding.error.not_https",
  contrast_too_low: "branding.error.contrast_too_low",
  image_unknown: "branding.error.image_unknown",
  image_too_large: "branding.error.image_too_large",
  image_dimensions: "branding.error.image_dimensions",
  master_signature_incomplete: "branding.error.master_signature_incomplete",
  timezone_unknown: "branding.error.timezone_unknown",
  language_code_invalid: "branding.error.language_code_invalid",
  wording_incomplete: "branding.error.wording_incomplete",
  placeholder_missing: "branding.error.placeholder_missing",
  placeholder_in_subject: "branding.error.placeholder_in_subject",
  placeholder_unknown: "branding.error.placeholder_unknown",
  placeholder_malformed: "branding.error.placeholder_malformed",
  domain_refused: "branding.error.domain_refused",
  format_invalid: "branding.error.format_invalid",
};

export function fieldErrorText(code: string): string {
  const key = ERROR_TEXT[code];
  return key !== undefined ? t(key) : code;
}

function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    return intl.supportedValuesOf?.("timeZone") ?? [];
  } catch {
    return [];
  }
}

export function BrandingPage({ mode }: { mode: BrandingMode }): JSX.Element {
  const { organizationId: own } = useIAMAuth();
  const orgs = useQuery({
    queryKey: ["branding-organizations"],
    queryFn: listBrandingOrganizations,
    retry: false,
  });
  const rows = useMemo(
    () => (mode === "portal" ? withinReach(orgs.data ?? [], own) : (orgs.data ?? [])),
    [mode, orgs.data, own],
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [typedId, setTypedId] = useState("");

  useEffect(() => {
    if (selected !== null) return;
    if (mode === "portal" && own !== null && rows.some((r) => r.organization_id === own)) {
      setSelected(own);
    } else if (rows.length > 0 && rows[0] !== undefined) {
      setSelected(rows[0].organization_id);
    }
  }, [mode, own, rows, selected]);

  const nameOf = (id: string | null): string => {
    if (id === null) return t("branding.default_source");
    // Names come from every row the server listed, not only the ones offered for editing,
    // so a value inherited from a parent shows the parent's name when the server sent it.
    const row = (orgs.data ?? []).find((r) => r.organization_id === id);
    return row?.name ?? id;
  };

  const title = mode === "portal" ? t("branding.portal_title") : t("branding.admin_title");
  return (
    <AppLayout
      breadcrumbs={
        mode === "portal"
          ? [{ label: t("portal.title"), to: "/portal" }, { label: title }]
          : [{ label: title }]
      }
    >
      <Title order={1}>{title}</Title>
      <Text c="dimmed">{t("branding.subtitle")}</Text>

      <Card withBorder data-testid="branding-org-picker">
        {orgs.isPending ? (
          <TableSkeleton label={t("branding.organizations_loading")} />
        ) : orgs.isError ? (
          <Alert color="red" title={t("branding.organizations_unavailable")}>
            {orgs.error instanceof Error ? orgs.error.message : null}
          </Alert>
        ) : (
          <Group align="flex-end">
            <Select
              label={t("branding.organization")}
              data={rows.map((r) => ({
                value: r.organization_id,
                label: `${r.name ?? r.organization_id}${r.has_branding ? "" : ` (${t("branding.unbranded")})`}`,
              }))}
              value={selected}
              onChange={setSelected}
              allowDeselect={false}
              searchable
              nothingFoundMessage={t("branding.organizations_none")}
              w={360}
              data-testid="branding-org-select"
            />
            {mode === "admin" && (
              <>
                <TextInput
                  label={t("branding.organization_id")}
                  value={typedId}
                  onChange={(e) => setTypedId(e.currentTarget.value.trim())}
                  w={320}
                  data-testid="branding-org-id"
                />
                <Button
                  variant="light"
                  disabled={typedId === ""}
                  onClick={() => setSelected(typedId)}
                  data-testid="branding-org-open"
                >
                  {t("branding.organization_open")}
                </Button>
              </>
            )}
          </Group>
        )}
      </Card>

      {selected !== null && (
        <BrandingEditor
          key={selected}
          organizationId={selected}
          nameOf={nameOf}
          listClinicians={mode === "portal" && selected === own}
        />
      )}
    </AppLayout>
  );
}

function BrandingEditor({
  organizationId,
  nameOf,
  listClinicians,
}: {
  organizationId: string;
  nameOf: (id: string | null) => string;
  listClinicians: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  // Held here, not in the form: a save bumps the version, which remounts the form on the
  // new record, and the confirmation must survive that.
  const [saved, setSaved] = useState<number | null>(null);
  const record = useQuery({
    queryKey: ["branding", organizationId],
    queryFn: () => getBranding(organizationId),
    retry: false,
  });

  if (record.isPending) {
    return <TableSkeleton label={t("branding.loading")} />;
  }
  if (record.isError) {
    return (
      <Alert color="red" title={t("branding.unavailable")} data-testid="branding-load-error">
        {record.error instanceof Error ? record.error.message : null}
      </Alert>
    );
  }
  return (
    <>
      {saved !== null && (
        <Alert
          color="green"
          data-testid="branding-saved"
          withCloseButton
          onClose={() => setSaved(null)}
        >
          {t("branding.saved")} {saved}
        </Alert>
      )}
      <BrandingForm
        key={`${organizationId}:${record.data.version ?? 0}`}
        organizationId={organizationId}
        record={record.data}
        nameOf={nameOf}
        onSaving={() => setSaved(null)}
        onSaved={setSaved}
        reload={() => queryClient.invalidateQueries({ queryKey: ["branding", organizationId] })}
      />
      <DoctorSignaturesCard organizationId={organizationId} listClinicians={listClinicians} />
    </>
  );
}

interface BrandingFormProps {
  organizationId: string;
  record: BrandingRecord;
  nameOf: (id: string | null) => string;
  onSaving: () => void;
  onSaved: (version: number) => void;
  reload: () => Promise<void>;
}

function BrandingForm({
  organizationId,
  record,
  nameOf,
  onSaving,
  onSaved,
  reload,
}: BrandingFormProps): JSX.Element {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<BrandingDraft>(() => draftOf(record.own));
  const [uploads, setUploads] = useState<Record<string, UploadedImage>>({});
  const [errors, setErrors] = useState<BrandingFieldError[]>([]);
  const fields = useMemo(() => fieldsOf(draft), [draft]);
  const info = imageInfoOf(record, uploads);
  const total = imageTotal(draft, record, info, organizationId);

  const save = useMutation({
    mutationFn: () => putBranding(organizationId, record.version, fields),
    onMutate: () => {
      setErrors([]);
      onSaving();
    },
    onSuccess: async (answer) => {
      onSaved(answer.version);
      await queryClient.invalidateQueries({ queryKey: ["branding-organizations"] });
      await queryClient.invalidateQueries({ queryKey: ["branding-versions", organizationId] });
      await reload();
    },
    onError: (err) => {
      if (err instanceof BrandingApiError) setErrors(err.fieldErrors);
    },
  });

  const errorFor = (path: string): string | null => {
    const hit = errors.find((e) => e.field === path);
    return hit !== undefined ? fieldErrorText(hit.code) : null;
  };

  const setText = (field: TextField, value: string): void =>
    setDraft((d) => ({ ...d, text: { ...d.text, [field]: value } }));
  const setImage = (field: ImageFieldName, sha: string): void =>
    setDraft((d) => ({ ...d, images: { ...d.images, [field]: sha } }));
  const setSwitch = (field: SwitchField, value: SwitchValue): void =>
    setDraft((d) => ({ ...d, switches: { ...d.switches, [field]: value } }));

  const inheritNote = (unit: string): string | null => {
    const from = inherited(record, unit, organizationId);
    if (from === null) return null;
    const value =
      typeof from.value === "string" || typeof from.value === "boolean"
        ? String(from.value)
        : JSON.stringify(from.value);
    return `${t("branding.inherited_from")} ${nameOf(from.source)}: ${value}`;
  };

  const text = (
    field: TextField,
    label: MessageKey,
    max: number,
    hint?: MessageKey,
  ): JSX.Element => (
    <TextInput
      label={t(label)}
      description={hint !== undefined ? t(hint) : inheritNote(field)}
      placeholder={inheritNote(field) === null ? undefined : t("branding.inherits_placeholder")}
      value={draft.text[field]}
      onChange={(e) => setText(field, e.currentTarget.value)}
      error={errorFor(field)}
      maxLength={max}
      data-testid={`branding-${field}`}
    />
  );

  const imageField = (
    field: ImageFieldName,
    slot: "logo" | "stamp" | "master_signature",
    label: MessageKey,
    unit: string,
  ): JSX.Element => {
    const sha = effectiveImage(draft, record, field, organizationId);
    const own = draft.images[field] !== "";
    const from = own ? null : inherited(record, unit, organizationId);
    return (
      <ImageField
        label={t(label)}
        slot={slot}
        sha256={sha}
        inheritedFrom={from !== null ? nameOf(from.source) : null}
        info={sha !== null ? info[sha] : undefined}
        upload={(file) => uploadBrandingImage(organizationId, slot, file)}
        onUploaded={(image) => {
          setUploads((u) => ({ ...u, [image.sha256]: image }));
          setImage(field, image.sha256);
        }}
        {...(own ? { onRemove: () => setImage(field, "") } : {})}
        error={errorFor(field)}
        testId={`branding-image-${slot}`}
      />
    );
  };

  const inheritedLanguages = Object.entries(record.resolved)
    .filter(
      ([unit, value]) =>
        unit.startsWith("email_wording.") &&
        value.source_organization_id !== organizationId &&
        !(unit.slice("email_wording.".length) in draft.wording),
    )
    .map(([unit, value]) => ({
      language: unit.slice("email_wording.".length),
      source: nameOf(value.source_organization_id),
    }));

  const emailLanguages = emailLanguagesOf(record, fields);
  const unknownErrors = errors.filter((e) => !isShownInline(e.field));

  return (
    <Stack gap="md" data-testid="branding-form">
      <Group gap="sm">
        <Text size="sm" c="dimmed">
          {record.version === null
            ? t("branding.version_none")
            : `${t("branding.version")} ${record.version}`}
        </Text>
      </Group>

      <Card withBorder>
        <Title order={3} mb="xs">
          {t("branding.section_names")}
        </Title>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          {text("display_name", "branding.display_name", 120)}
          <div dir="rtl">{text("display_name_ar", "branding.display_name_ar", 120)}</div>
          {text("sender_name", "branding.sender_name", 120)}
          <div dir="rtl">{text("sender_name_ar", "branding.sender_name_ar", 120)}</div>
        </SimpleGrid>
      </Card>

      <Card withBorder>
        <Title order={3} mb="xs">
          {t("branding.section_look")}
        </Title>
        <Stack gap="md">
          {imageField("logo_sha256", "logo", "branding.logo", "logo")}
          <ColorFields
            primary={draft.text.primary_color}
            ink={draft.text.ink_color}
            onPrimary={(v) => setText("primary_color", v)}
            onInk={(v) => setText("ink_color", v)}
            inheritedNote={inheritNote("colors")}
            primaryError={errorFor("primary_color")}
            inkError={errorFor("ink_color")}
          />
        </Stack>
      </Card>

      <Card withBorder>
        <Title order={3} mb="xs">
          {t("branding.section_contact")}
        </Title>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          {text("contact_email", "branding.contact_email", 254)}
          {text("contact_phone", "branding.contact_phone", 40, "branding.contact_phone_hint")}
          <Textarea
            label={t("branding.contact_address")}
            description={inheritNote("contact_address") ?? t("branding.contact_address_hint")}
            autosize
            minRows={2}
            maxRows={4}
            value={draft.text.contact_address}
            onChange={(e) => setText("contact_address", e.currentTarget.value)}
            error={errorFor("contact_address")}
            maxLength={300}
            data-testid="branding-contact_address"
          />
          {text("contact_website", "branding.contact_website", 500, "branding.https_hint")}
        </SimpleGrid>
      </Card>

      <Card withBorder>
        <Title order={3} mb="xs">
          {t("branding.section_legal")}
        </Title>
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          {text("terms_url", "branding.terms_url", 500, "branding.https_hint")}
          {text("privacy_url", "branding.privacy_url", 500, "branding.https_hint")}
          {text("footer_line", "branding.footer_line", 200)}
          <Autocomplete
            label={t("branding.timezone")}
            description={inheritNote("timezone") ?? t("branding.timezone_hint")}
            data={timeZones()}
            limit={20}
            value={draft.text.timezone}
            onChange={(v) => setText("timezone", v)}
            error={errorFor("timezone")}
            data-testid="branding-timezone"
          />
        </SimpleGrid>
      </Card>

      <Card withBorder>
        <Title order={3} mb="xs">
          {t("branding.section_signatures")}
        </Title>
        <Stack gap="md">
          {imageField("stamp_sha256", "stamp", "branding.stamp", "stamp")}
          <Text size="sm" c="dimmed">
            {t("branding.master_signature_hint")}
          </Text>
          {text("master_signature_holder_name", "branding.master_signature_holder", 120)}
          {imageField(
            "master_signature_sha256",
            "master_signature",
            "branding.master_signature",
            "master_signature",
          )}
          {errorFor("master_signature") && (
            <Alert color="red" p="xs">
              {errorFor("master_signature")}
            </Alert>
          )}
        </Stack>
      </Card>

      <Card withBorder>
        <Title order={3} mb="xs">
          {t("branding.section_report_details")}
        </Title>
        <Stack gap="sm">
          {SWITCH_FIELDS.map((field) => (
            <Group key={field} justify="space-between" wrap="nowrap">
              <Stack gap={0}>
                <Text size="sm">{t(SWITCH_LABEL[field])}</Text>
                {draft.switches[field] === "inherit" && (
                  <Text size="xs" c="dimmed">
                    {inheritNote(field) ?? t("branding.switch_default_on")}
                  </Text>
                )}
              </Stack>
              <SegmentedControl
                size="xs"
                value={draft.switches[field]}
                onChange={(v) => setSwitch(field, v as SwitchValue)}
                data={[
                  { value: "inherit", label: t("branding.switch_inherit") },
                  { value: "show", label: t("branding.switch_show") },
                  { value: "hide", label: t("branding.switch_hide") },
                ]}
                data-testid={`branding-${field}`}
              />
            </Group>
          ))}
          {text("prepared_by", "branding.prepared_by", 120, "branding.prepared_by_hint")}
        </Stack>
      </Card>

      <Card withBorder>
        <Title order={3} mb="xs">
          {t("branding.section_wording")}
        </Title>
        <WordingEditor
          wording={draft.wording}
          onChange={(wording) => setDraft((d) => ({ ...d, wording }))}
          errorFor={errorFor}
          inheritedLanguages={inheritedLanguages}
        />
      </Card>

      <Card withBorder data-testid="branding-image-total">
        <Group justify="space-between">
          <Text fw={600}>{t("branding.image_total")}</Text>
          <Text data-testid="branding-image-total-value">
            {formatBytes(total.knownBytes)} / {formatBytes(DELIVERY_IMAGE_BUDGET_BYTES)}
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          {t("branding.image_total_hint").replace(
            "{reserve}",
            formatBytes(total.approverReserveBytes),
          )}
        </Text>
        {total.unknownSlots.length > 0 && (
          <Text size="xs" c="dimmed" data-testid="branding-image-total-unknown">
            {t("branding.image_total_unknown")}
          </Text>
        )}
      </Card>

      {unknownErrors.length > 0 && (
        <Alert color="red" title={t("branding.save_refused")} data-testid="branding-errors">
          <Stack gap={2}>
            {unknownErrors.map((e) => (
              <Text key={`${e.field}:${e.code}`} size="sm">
                <Code>{e.field}</Code> {fieldErrorText(e.code)}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}
      {save.isError && (
        <Alert
          color={
            save.error instanceof BrandingApiError &&
            save.error.code === "branding_version_conflict"
              ? "orange"
              : "red"
          }
          data-testid="branding-save-error"
        >
          <Stack gap="xs">
            <Text size="sm">{save.error.message}</Text>
            {save.error instanceof BrandingApiError &&
              save.error.code === "branding_version_conflict" && (
                <Button size="xs" variant="light" onClick={() => void reload()}>
                  {t("branding.reload")}
                </Button>
              )}
          </Stack>
        </Alert>
      )}
      <Group>
        <Button onClick={() => save.mutate()} loading={save.isPending} data-testid="branding-save">
          {t("branding.save")}
        </Button>
        <Text size="xs" c="dimmed">
          {t("branding.save_hint")}
        </Text>
      </Group>

      <PreviewPanel
        organizationId={organizationId}
        fields={fields}
        emailLanguages={emailLanguages}
      />
      <DomainsCard
        organizationId={organizationId}
        sending={record.sending_domain}
        link={record.link_domain}
      />
      <VersionHistory organizationId={organizationId} />
    </Stack>
  );
}

const SWITCH_LABEL: Record<SwitchField, MessageKey> = {
  show_approved_by: "branding.show_approved_by",
  show_approver_email: "branding.show_approver_email",
  show_prepared_by: "branding.show_prepared_by",
};

const INLINE = new Set<string>([
  "display_name",
  "display_name_ar",
  "sender_name",
  "sender_name_ar",
  "contact_email",
  "contact_phone",
  "contact_address",
  "contact_website",
  "terms_url",
  "privacy_url",
  "footer_line",
  "timezone",
  "primary_color",
  "ink_color",
  "logo_sha256",
  "stamp_sha256",
  "master_signature_sha256",
  "master_signature",
  "master_signature_holder_name",
  "prepared_by",
]);

/** Field codes shown next to their field. Everything else is listed above the save button. */
function isShownInline(field: string): boolean {
  return INLINE.has(field) || field.startsWith("email_wording.");
}

/**
 * The languages the email preview offers: English, Arabic, and every language with a
 * wording set in the draft or inherited. The server renders each one as it would be sent.
 */
function emailLanguagesOf(record: BrandingRecord, fields: ReturnType<typeof fieldsOf>): string[] {
  const languages = new Set(["en", "ar"]);
  for (const unit of Object.keys(record.resolved)) {
    if (unit.startsWith("email_wording.")) languages.add(unit.slice("email_wording.".length));
  }
  for (const language of Object.keys(fields.email_wording ?? {})) languages.add(language);
  return Array.from(languages);
}

function VersionHistory({ organizationId }: { organizationId: string }): JSX.Element {
  const versions = useQuery({
    queryKey: ["branding-versions", organizationId],
    queryFn: () => listBrandingVersions(organizationId),
    retry: false,
  });
  return (
    <Card withBorder data-testid="branding-versions">
      <Title order={3} mb="xs">
        {t("branding.versions_title")}
      </Title>
      {versions.isPending ? (
        <Text size="sm" c="dimmed">
          {t("branding.versions_loading")}
        </Text>
      ) : versions.isError ? (
        <Text size="sm" c="dimmed">
          {t("branding.versions_unavailable")}
        </Text>
      ) : versions.data.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("branding.versions_none")}
        </Text>
      ) : (
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("branding.version")}</Table.Th>
              <Table.Th>{t("branding.versions_when")}</Table.Th>
              <Table.Th>{t("branding.versions_who")}</Table.Th>
              <Table.Th>{t("branding.versions_changed")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {versions.data.map((v) => (
              <Table.Tr key={v.version}>
                <Table.Td>{v.version}</Table.Td>
                <Table.Td>{new Date(v.created_at).toLocaleString()}</Table.Td>
                <Table.Td>
                  <Badge variant="light" color="gray">
                    {v.actor_kind}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{v.changed_fields.join(", ")}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Card>
  );
}
